import * as React from 'react'
import { Users, X, Mail, Phone, MessageCircle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const ContactWidget: React.FC = () => {
  const { t, isRTL } = useLanguage()
  const [isContactOpen, setIsContactOpen] = React.useState(false)

  return (
    <div className={`fixed bottom-6 z-40 font-arabic ${isRTL ? 'right-6' : 'left-6'}`}>
      {/* Floating Contact Button */}
      <button 
        onClick={() => setIsContactOpen(!isContactOpen)}
        className={`text-white px-4 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} group`}
        style={{ backgroundColor: '#0075c9' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005a9e'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0075c9'}
      >
        <div className="relative">
          <Users className="w-5 h-5" />
          <div className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} w-3 h-3 bg-red-500 rounded-full animate-pulse`}></div>
        </div>
        <span className="font-medium">{t('contact.widget.button' as any)}</span>
      </button>

      {/* Contact Widget Popup */}
      {isContactOpen && (
        <div className={`absolute bottom-full mb-4 w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 ${isRTL ? 'right-0' : 'left-0'}`}>
          {/* Header */}
          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''} justify-between p-4 border-b border-gray-200 dark:border-gray-700`}>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900`}></div>
              </div>
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <div className="font-semibold text-gray-900 dark:text-white">{t('contact.widget.support' as any)}</div>
                <div className="text-sm text-green-600 dark:text-green-400">{t('contact.widget.online' as any)}</div>
              </div>
            </div>
            <button 
              onClick={() => setIsContactOpen(false)}
              className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-300 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Chat Message */}
          <div className="p-4">
            <div className={`${isRTL ? 'text-right' : 'text-center'} text-xs text-gray-500 dark:text-gray-400 mb-3`}>
              {new Date().toLocaleTimeString(isRTL ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
            
            <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mb-4`}>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div className={`bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3 max-w-xs ${isRTL ? 'text-right' : 'text-left'}`}>
                <p className="text-gray-900 dark:text-white text-sm">
                  {t('contact.widget.message' as any)}
                </p>
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <a 
                href="https://wa.me/966555689485"
                target="_blank"
                rel="noopener noreferrer"
                className={`bg-green-500 hover:bg-green-600 text-white p-3 rounded-xl ${isRTL ? 'text-right' : 'text-center'} transition-colors duration-300 flex flex-col items-center space-y-1`}
              >
                <MessageCircle className="w-6 h-6" />
                <span className="text-xs font-medium">{t('contact.widget.whatsapp' as any)}</span>
              </a>
              
              <a 
                href="tel:+966555689485"
                className={`bg-green-500 hover:bg-green-600 text-white p-3 rounded-xl ${isRTL ? 'text-right' : 'text-center'} transition-colors duration-300 flex flex-col items-center space-y-1`}
              >
                <Phone className="w-6 h-6" />
                <span className="text-xs font-medium">{t('contact.widget.call' as any)}</span>
              </a>
              
              <a 
                href="mailto:info@fmcksa.com"
                className={`text-white p-3 rounded-xl ${isRTL ? 'text-right' : 'text-center'} transition-colors duration-300 flex flex-col items-center space-y-1`}
                style={{ backgroundColor: '#0075c9' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005a9e'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0075c9'}
              >
                <Mail className="w-6 h-6" />
                <span className="text-xs font-medium">{t('contact.widget.email' as any)}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactWidget
